import React, { useState, useEffect, FC } from "react";
import { Select, Button, Text, Group, TextInput, Loader, LoadingOverlay } from "@mantine/core";
import { FileType, LevelType, SubjectType } from "src/interfaces/KnowledgeBase";
import { knowledgeBaseService } from "src/services/services/knowledgeBase/knowledgeBase.api";
import { useManualDocsUpload } from "src/hooks/useManualKnowledgeBaseDocsUpload";
import { useKnowledgebase } from "src/hooks/useKnowledgebase";
import { ManualUploadKnowledgeBaseEditor } from "./ManualUploadKnowledgeBaseEditor";
import { useQuestionForm } from "src/hooks/useManualQuestionBankDocsUpload";
import { QBQuestionFormEditor } from "./ManualUploadQuestionBankEditor";
import { useBackgroundNotifications } from "src/hooks/useBackgroundNotifications";

type ManualUploadFormProps = {
  onSuccess: (message?: string) => void;
  onError: (error: string) => void;
  sharedLevels?: LevelType[];
  isLoadingLevels?: boolean;
};

const ManualUploadForm: FC<ManualUploadFormProps> = ({
  onSuccess,
  onError,
  sharedLevels = [],
  isLoadingLevels = false,
}) => {
  const [type, setType] = useState<FileType | null>(null);
  const [level, setLevel] = useState<string>("");
  const [subject, setSubject] = useState<SubjectType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [subjects, setSubjects] = useState<SubjectType[]>([]);
  const [levels, setLevels] = useState<LevelType[]>(sharedLevels);
  const [fileName, setFileName] = useState<string>("");

  const {
    getDocs: onRefresh,
    getCombinedSummary,
    fetchingCombinedSummary,
    getUrlsToPostOnAzure,
    loading: kbLoading,
    uploadFilesToAzure,
    // setFileUrlsForQuestions,
    postQuestionBankPreparedData,
  } = useKnowledgebase(false);

  const {
    themes,
    config,
    addTheme,
    removeTheme,
    updateThemeName,
    addTopic,
    removeTopic,
    updateTopicName,
    addLearningObjective,
    removeLearningObjective,
    updateLearningObjectiveName,
    updateContent,
    validate,
    reset,
    setConfigFromStructure,
    getFormData,
  } = useManualDocsUpload();

  const {
    qbThemes,
    transformedQBData,
    qbConfig,
    addQBTheme,
    removeQBTheme,
    addQBTopic,
    removeQBTopic,
    addQBQuestion,
    removeQBQuestion,
    updateQBQuestion,
    validateQBForm,
    resetQBForm,
    getQBFormData,
    setQBConfigFromStructure,
    createTransformedQB,
    updateSelectedTheme,
    updateSelectedTopic,
    getAllQuestionFiles,
    updateThemesWithFileUrls,
    buildStructuredQBThemes,
    createConfigFromBackendProvidedStructure,
  } = useQuestionForm();

  const { showPlaceholderDrawer } = useBackgroundNotifications();

  // Update levels when sharedLevels prop changes
  useEffect(() => {
    if (sharedLevels.length > 0) {
      setLevels(sharedLevels);
    }
  }, [sharedLevels]);

  useEffect(() => {
    const fetchSubjectsForLevel = async () => {
      if (!level) {
        setSubjects([]);
        return;
      }
      try {
        setIsLoading(true);
        const res = await knowledgeBaseService.getSubjects(Number(level));
        if (res.kind === "ok") {
          setSubjects(res.data);
        } else {
          throw new Error("Failed to load subjects");
        }
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Failed to load subjects";
        setError(msg);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSubjectsForLevel();
  }, [level]);

  const fetchCombinedSummary = async () => {
    const levelName = levels.find((l) => l.id.toString() === level)!.name;
    const { data, error } = await getCombinedSummary(levelName, subject?.name);
    if (data) {
      createTransformedQB(data);
      setCurrentStep(2);
    } else {
      setError(error);
    }
  };

  const handleSubjectChange = (value: string | null) => {
    if (!value) {
      setSubject(null);
      return;
    }
    const selectedSubject = subjects.find((s) => s.id.toString() === value);
    if (selectedSubject) {
      setSubject(selectedSubject);
      setError(null);
      if (type === "Question Bank" && selectedSubject.structure_qb) {
        console.log(
          "selectedSubject.structure_qb",
          selectedSubject.structure_qb
        );
        createConfigFromBackendProvidedStructure(
          JSON.parse(selectedSubject.structure_qb)
        );
      } else if (type === "Knowledge" && selectedSubject.structure_kb) {
        console.log(
          "selectedSubject.structure_kb",
          selectedSubject.structure_kb
        );
        setConfigFromStructure(selectedSubject.structure_kb);
      }
    }
  };

  const handleLevelChange = (value: string | null) => {
    if (value) {
      setLevel(value);
      setSubject(null);
    }
  };

  const handleTypeChange = (value: FileType) => {
    setType(value);
    setError(null);
  };

  const getImageUrls = async () => {
    const fileMap = getAllQuestionFiles();
    const fileUUIDs = Object.keys(fileMap);

    if (!fileUUIDs.length) {
      setError("No files selected");
      return;
    }

    const imageUrls = await getUrlsToPostOnAzure(fileName, fileUUIDs);
    if (!imageUrls) {
      setError("Failed to get image urls");
      return;
    }

    // Merge signed URLs with files into a single map
    const uploadMap = Object.fromEntries(
      fileUUIDs.map((uuid) => [
        uuid,
        {
          uploadUrl: imageUrls[uuid].upload_url, // assuming your API returns this
          file: fileMap[uuid],
        },
      ])
    );
    const readUrls = Object.fromEntries(
      fileUUIDs.map((uuid) => [uuid, imageUrls[uuid].read_url]) // ensure backend provides this
    );
    return { uploadMap, readUrls };
  };

  const sendQuestionBankPreparedData = async (data: string) => {
    const levelName = levels.find((l) => l.id.toString() === level)?.name;
    const response = await postQuestionBankPreparedData({
      is_qb: true,
      level: levelName!,
      subject: subject!.name,
      summary: data,
      file_name: fileName,
    });

    if (response.kind === "ok") {
      // Show drawer immediately with placeholder
      showPlaceholderDrawer();
      onSuccess();
      reset();
      setFileName("");
      setCurrentStep(1);
    } else {
      setError(
        response.message || "Failed to send question bank prepared data"
      );
    }
  };

  const uploadImages = async () => {
    const result = await getImageUrls();
    if (!result) {
      setError("Failed to get image urls");
      return;
    }

    const { uploadMap, readUrls } = result;
    const uploadResult = await uploadFilesToAzure(uploadMap);
    if (uploadResult.kind !== "ok") {
      setError("Image upload failed!");
      return;
    }
    const updatedThemes = updateThemesWithFileUrls(readUrls);
    const buildStructuredQBThemesResult =
      buildStructuredQBThemes(updatedThemes);
    await sendQuestionBankPreparedData(buildStructuredQBThemesResult);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject || !level || !type) {
      setError("Please fill in all fields");
      return;
    }

    if (currentStep === 1) {
      if (type === "Question Bank") {
        await fetchCombinedSummary();
      } else {
        setCurrentStep(2);
      }
      return;
    }

    if (type === "Question Bank") {
      await uploadImages();
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const levelName = levels.find((l) => l.id.toString() === level)?.name;
      if (!levelName) {
        throw new Error("Level not found");
      }

      const response = await knowledgeBaseService.uploadManualFile(
        false,
        levelName,
        subject.name,
        JSON.stringify(getFormData()),
        fileName,
        false
      );

      if (response.kind !== "ok") {
        throw new Error("Upload failed");
      }

      // Show drawer immediately with placeholder
      showPlaceholderDrawer();
      onSuccess?.();
      // reset form
      reset();
      setFileName("");
      setSubject(null);
      setLevel("");
      setType(null);

      setCurrentStep(1);

      onRefresh();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Upload failed";
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderMainFields = () => (
    <div className="space-yt-2 pb-3 relative">
      <LoadingOverlay visible={isLoadingLevels} overlayBlur={2} />
      <Group position="apart" grow>
        <Select
          label="File Type"
          placeholder="Select file type"
          size="xs"
          data={[
            { value: "Question Bank", label: "Question Bank (QB)" },
            { value: "Knowledge", label: "Knowledge Base (KB)" },
          ]}
          value={type}
          onChange={handleTypeChange}
          required
          disabled={currentStep === 2 || isLoading || isLoadingLevels}
        />
        <Select
          label="Level"
          size="xs"
          placeholder={isLoadingLevels ? "Loading levels..." : "Select a level"}
          data={levels.map((l) => ({
            value: l.id.toString(),
            label: l.name,
          }))}
          value={level}
          onChange={handleLevelChange}
          required
          disabled={currentStep === 2 || isLoading || isLoadingLevels}
        />
        <Select
          label="Subject"
          size="xs"
          placeholder="Select a subject"
          data={subjects.map((s) => ({
            value: s.id.toString(),
            label: s.name,
          }))}
          value={subject?.id?.toString() || null}
          onChange={handleSubjectChange}
          required
          disabled={currentStep === 2 || isLoading || isLoadingLevels}
        />
      </Group>
      {currentStep === 2 && (
        <TextInput
          label="File Name"
          size="xs"
          value={fileName}
          onChange={(e) => setFileName(e.target.value)}
          required
          disabled={currentStep !== 2 || isLoading}
        />
      )}
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      <div>{renderMainFields()}</div>
      <div className="overflow-y-auto max-h-[calc(100vh-520px)] lg:max-h-[calc(100vh-420px)]">
        {currentStep === 2 && (
          <>
            {isLoading ? (
              <Loader />
            ) : type === "Question Bank" ? (
              <QBQuestionFormEditor
                data={{
                  qbThemes,
                  transformedQBData,
                  qbConfig,
                  addQBTheme,
                  removeQBTheme,
                  addQBTopic,
                  removeQBTopic,
                  addQBQuestion,
                  removeQBQuestion,
                  updateQBQuestion,
                  validateQBForm,
                  resetQBForm,
                  getQBFormData,
                  setQBConfigFromStructure,
                  // prefillQBForm,
                  createTransformedQB,
                  updateSelectedTheme,
                  updateSelectedTopic,
                  // updateSelectedLO,
                }}
              />
            ) : (
              <ManualUploadKnowledgeBaseEditor
                data={{
                  config,
                  themes,
                  addTheme,
                  removeTheme,
                  updateThemeName,
                  addTopic,
                  removeTopic,
                  updateTopicName,
                  addLearningObjective,
                  removeLearningObjective,
                  updateLearningObjectiveName,
                  updateContent,
                }}
              />
            )}
          </>
        )}
        {error && (
          <Text color="red" size="sm" mt="sm">
            {error}
          </Text>
        )}
      </div>

      <div className="pb-6">
        <div className="flex flex-col gap-2 lg:flex-row lg:justify-end mt-6">
          {currentStep === 2 && (
            <Button
              variant="default"
              onClick={() => {
                setCurrentStep(1);
                resetQBForm();
              }}
              disabled={isLoading}
              className="w-full lg:w-auto"
            >
              Back
            </Button>
          )}
          <Button
            type="submit"
            loading={isLoading || fetchingCombinedSummary || kbLoading}
            disabled={
              !subject ||
              !level ||
              !type ||
              (currentStep === 2 && !fileName) ||
              (currentStep === 2 &&
                (type === "Question Bank" ? !validateQBForm() : !validate())) ||
              !!error
            }
            className="w-full lg:w-auto"
          >
            {currentStep === 1 ? "Next" : "Upload"}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ManualUploadForm;
